import { type ReactNode, type Ref } from 'react';
import { cn } from '@/utils/tailwind.util';
import { DetailHeroBanner } from './DetailHeroBanner';
export interface DetailPageLayoutProps {
  /** 상단 전체 너비 배너 이미지 */
  /** 없으면 `DetailHeroBanner` 가 기본 그라데이션 배경을 그린다. */
  bannerUrl?: string;
  /** 배너 alt. 장식용이면 빈 문자열 */
  bannerAlt?: string;
  /** 배너에 60px 겹쳐 올라오는 히어로 카드 */
  hero: ReactNode;
  /** 우측 250px 사이드. 없으면 본문이 전폭을 쓴다 */
  aside?: ReactNode;
  /**
   * 모바일에서 `aside` 를 숨길지. **기본 true** — 퀴즈 상세는 하단 고정 CTA 푸터가
   * 그 자리를 대신하기 때문이다. 대체 수단이 아직 없는 화면은 `false` 로 두어야
   * CTA 가 모바일에서 통째로 사라지지 않는다.
   */
  hideAsideOnMobile?: boolean;
  /** 바깥 컨테이너에 덧붙일 클래스 (예: 로그인 가드가 하단 여백을 줄이는 경우) */
  containerClassName?: string;
  /** 본문 영역 전체를 관찰해야 할 때 (스크롤 기반 서브헤더 등) */
  mainRef?: Ref<HTMLDivElement>;
  /** 좌측 732px 본문 */
  children: ReactNode;
}

/**
 * 상세 화면 셸 — 배너 · 1024 컨테이너 · 히어로 겹침 · 732+250 2단.
 *
 * 퀴즈 상세(`/quiz/[id]`)와 폴더 상세(`/folder/[id]`)가 **같은 골격**을 쓴다.
 * 각자 두었더니 배너의 `relative` 와 그라데이션 농도, aside 의 모바일 동작이 조용히
 * 어긋났다 — 값이 한 세트의 디자인 결정이므로 여기서 한 곳이 소유한다.
 *
 * 페이지가 채우는 건 **슬롯 3개**(`hero` · `children` · `aside`)뿐이다.
 * `aside` 안쪽 구조(sticky 위치, 여러 블록, ref)는 화면마다 달라 슬롯째로 받는다 —
 * 퀴즈는 CTA 와 워크시트 프로모 두 덩어리를 스크롤 위치까지 계산해 붙인다.
 *
 * 수치는 Figma 상세 시안 공통값이다: 배너 86/136, 컨테이너 1024,
 * 히어로 겹침 -42/-60, 2단 간격 42, 본문 732, 사이드 250, sticky top 140.
 */
export const DetailPageLayout = ({
  bannerUrl,
  bannerAlt = '',
  hero,
  aside,
  hideAsideOnMobile = true,
  containerClassName,
  mainRef,
  children
}: DetailPageLayoutProps) => {
  return <>
      <DetailHeroBanner bannerUrl={bannerUrl} alt={bannerAlt} data-sentry-element="DetailHeroBanner" data-sentry-source-file="DetailPageLayout.tsx" />

      <div className={cn('mx-auto mb-[150px] w-full max-w-[1024px] px-md', 'mobile-lg:px-lg', 'tablet-lg:mb-[100px] tablet-lg:px-0', containerClassName)}>
        {/* 배너 위로 끌어올려 히어로 카드가 겹치게 한다. */}
        <div className={'-mt-[42px] tablet-sm:-mt-[60px]'}>
          <div ref={mainRef} className={'flex flex-col gap-lg'}>
            <div className={'relative'}>{hero}</div>

            <div className={'flex flex-col gap-lg tablet-sm:flex-row tablet-sm:gap-[42px]'}>
              <div className={'flex-1 tablet-sm:w-[732px]'}>{children}</div>

              {aside != null && <aside className={cn('shrink-0 tablet-sm:flex tablet-sm:w-[250px] tablet-sm:flex-col', hideAsideOnMobile ? 'hidden' : 'flex w-full')}>
                  {aside}
                </aside>}
            </div>
          </div>
        </div>
      </div>
    </>;
};